import React from 'react';

/**
 * Larynx Caterpillar — mascot component.
 *
 * A plump amber caterpillar in side-profile with large expressive eyes,
 * microphone-tipped antennae, internal warm glow, and animated sound waves.
 *
 * Props:
 *   mood: 'happy' | 'excited' | 'thinking' | 'celebrating' | 'peeking' | 'sad'
 *   size: number (px, default 80)
 *   className: string
 *   message: string (speech bubble text)
 */

const AMBER      = 'var(--accent)';
const AMBER_DARK = '#C47D0E';
const AMBER_MID  = '#E8961F';
const INDIGO     = '#6B67FF';
const BLUSH      = '#FF9F7A';
const ERR_RED    = '#E05252';

export default function Larry({ mood = 'happy', size = 80, className = '', message }) {
  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          viewBox="0 0 120 120"
          width={size}
          height={size}
          className={getMoodAnimation(mood)}
          role="img"
          aria-label={`Caterpillar mascot — ${mood}`}
          style={{ overflow: 'hidden' }}
        >
          <defs>
            {/* Warm inner glow — applied to every segment as a soft internal light */}
            <radialGradient id="segGlow" cx="38%" cy="32%" r="62%">
              <stop offset="0%"   stopColor="white" stopOpacity="0.28" />
              <stop offset="100%" stopColor="white" stopOpacity="0"    />
            </radialGradient>
          </defs>

          <g transform={mood === 'peeking' ? 'translate(0, 55)' : undefined}>

            {/* Ground shadow */}
            {mood !== 'peeking' && (
              <ellipse cx="50" cy="91" rx="40" ry="5" fill="rgba(0,0,0,0.15)" />
            )}

            {/* ── Segment 5 — tail ── */}
            <circle cx="20" cy="70" r="9"  fill="#A86A0A" />
            <circle cx="20" cy="70" r="9"  fill="url(#segGlow)" />
            <ellipse cx="20" cy="74.5" rx="7"  ry="3.5" fill="rgba(0,0,0,0.2)" />

            {/* ── Segment 4 ── */}
            <circle cx="33" cy="70" r="11" fill={AMBER_DARK} />
            <circle cx="33" cy="70" r="11" fill="url(#segGlow)" />
            <ellipse cx="33" cy="75.5" rx="8.5" ry="4"   fill="rgba(0,0,0,0.2)" />
            <circle cx="32" cy="66"  r="3.5" fill={INDIGO} />
            <circle cx="30.5" cy="64.5" r="1.3" fill="white" opacity="0.85" />

            {/* ── Segment 3 ── */}
            <circle cx="47" cy="66" r="13" fill="#D07F15" />
            <circle cx="47" cy="66" r="13" fill="url(#segGlow)" />
            <ellipse cx="47" cy="72"   rx="10"  ry="5"   fill="rgba(0,0,0,0.2)" />
            <circle cx="46" cy="62"  r="4"   fill={INDIGO} />
            <circle cx="44.5" cy="60.5" r="1.5" fill="white" opacity="0.85" />

            {/* ── Segment 2 ── */}
            <circle cx="63" cy="61" r="15" fill={AMBER_MID} />
            <circle cx="63" cy="61" r="15" fill="url(#segGlow)" />
            <ellipse cx="63" cy="68"   rx="12"  ry="5.5" fill="rgba(0,0,0,0.2)" />
            <circle cx="61" cy="57"  r="4.5" fill={INDIGO} />
            <circle cx="59.5" cy="55.5" r="1.7" fill="white" opacity="0.85" />

            {/* ── Legs ── */}
            <rect x="59" y="74" width="8" height="9" rx="3" fill={AMBER_DARK} />
            <rect x="43" y="77" width="8" height="9" rx="3" fill={AMBER_DARK} />
            <rect x="28" y="79" width="7" height="8" rx="3" fill={AMBER_DARK} />
            <rect x="15" y="77" width="6" height="7" rx="3" fill={AMBER_DARK} />

            {/* ── Head ── */}
            <circle cx="80" cy="47" r="19" fill={AMBER} />
            <circle cx="80" cy="47" r="19" fill="url(#segGlow)" />
            <ellipse cx="80" cy="58" rx="14" ry="7"   fill="rgba(0,0,0,0.13)" />
            {/* Blush */}
            <ellipse cx="87" cy="54" rx="5"  ry="3"   fill={BLUSH} opacity="0.45" />
            <ellipse cx="79" cy="57" rx="4"  ry="2.5" fill={BLUSH} opacity="0.3"  />

            <Antennae mood={mood} />
            <Eye mood={mood} />
            <SoundWaves mood={mood} />
            <Mouth mood={mood} />

            {mood === 'celebrating' && <PartyHat />}
            {mood === 'excited'     && <Sparkles />}
          </g>
        </svg>

        {/* Confetti overlay */}
        {mood === 'celebrating' && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full animate-confetti"
                style={{
                  left: `${15 + i * 13}%`,
                  top: '5%',
                  backgroundColor: [AMBER, INDIGO, ERR_RED, AMBER, INDIGO, AMBER_DARK][i],
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Speech bubble */}
      {message && (
        <div
          className="relative mt-1 px-3 py-1.5 text-xs font-bold max-w-[180px] text-center animate-bounce-in"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '1.25rem',
            backdropFilter: 'blur(8px)',
            color: 'var(--text-primary)',
          }}
        >
          <div
            className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45"
            style={{
              background: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border-subtle)',
              borderTop: '1px solid var(--border-subtle)',
            }}
          />
          <span className="relative">{message}</span>
        </div>
      )}
    </div>
  );
}

// ─── Mic tip — replaces plain circle at each antenna end ─────────────────────
// Styled as a ball condenser mic: sphere body + dot grill + shine highlight

function MicTip({ cx, cy }) {
  return (
    <g>
      {/* Mic sphere */}
      <circle cx={cx} cy={cy} r={5} fill={AMBER_DARK} />
      {/* Grill dot pattern (4-point cross) */}
      <circle cx={cx}     cy={cy - 2} r={0.65} fill="rgba(0,0,0,0.28)" />
      <circle cx={cx - 2} cy={cy}     r={0.65} fill="rgba(0,0,0,0.28)" />
      <circle cx={cx + 2} cy={cy}     r={0.65} fill="rgba(0,0,0,0.28)" />
      <circle cx={cx}     cy={cy + 2} r={0.65} fill="rgba(0,0,0,0.28)" />
      {/* Shine */}
      <circle cx={cx - 1.5} cy={cy - 1.5} r={1.3} fill="white" opacity="0.35" />
    </g>
  );
}

// ─── Sound waves — arcs radiating from the mouth area ────────────────────────
// Hidden for sad/peeking. Faint for thinking. Animated opacity pulse.

function SoundWaves({ mood }) {
  if (mood === 'sad' || mood === 'peeking') return null;

  const isLoud      = mood === 'excited' || mood === 'celebrating';
  const baseOpacity = mood === 'thinking' ? 0.18 : 0.55;

  // Wave arcs: M x,y1 A r,r 0 0 1 x,y2  →  sweep clockwise = bulges right
  // Wave 1: semicircle r=5 (peak at x=106)
  // Wave 2: arc r=8      (peak at x≈117)
  // Wave 3: arc r=10     (peak at x≈123, clips at 120 — fine)
  return (
    <g>
      <path
        d="M 101,47 A 5,5 0 0 1 101,57"
        stroke={AMBER} strokeWidth="2.2" fill="none" strokeLinecap="round"
        opacity={baseOpacity}
      >
        <animate
          attributeName="opacity"
          values={`${baseOpacity};${baseOpacity * 0.25};${baseOpacity}`}
          dur="2s" begin="0s" repeatCount="indefinite"
        />
      </path>

      <path
        d="M 105,45 A 8,8 0 0 1 105,59"
        stroke={AMBER} strokeWidth="1.8" fill="none" strokeLinecap="round"
        opacity={baseOpacity * 0.65}
      >
        <animate
          attributeName="opacity"
          values={`${baseOpacity * 0.65};${baseOpacity * 0.18};${baseOpacity * 0.65}`}
          dur="2s" begin="0.35s" repeatCount="indefinite"
        />
      </path>

      {isLoud && (
        <path
          d="M 109,43 A 10,10 0 0 1 109,61"
          stroke={AMBER} strokeWidth="1.5" fill="none" strokeLinecap="round"
          opacity={baseOpacity * 0.38}
        >
          <animate
            attributeName="opacity"
            values={`${baseOpacity * 0.38};${baseOpacity * 0.1};${baseOpacity * 0.38}`}
            dur="2s" begin="0.7s" repeatCount="indefinite"
          />
        </path>
      )}
    </g>
  );
}

// ─── Antennae — mic-tipped curves ────────────────────────────────────────────
// Sad mood droops → plain circles (drooping mics look awkward)

function Antennae({ mood }) {
  const isPerked   = mood === 'excited' || mood === 'celebrating';
  const isSad      = mood === 'sad';
  const isThinking = mood === 'thinking';

  if (isPerked) {
    return (
      <g>
        <path d="M 76,30 C 73,18 76,10 71,5"  stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <MicTip cx={71} cy={5} />
        <path d="M 84,28 C 87,16 90,8  87,4"  stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <MicTip cx={87} cy={4} />
      </g>
    );
  }

  if (isSad) {
    // Drooping — plain circles only
    return (
      <g>
        <path d="M 76,30 C 67,33 62,38 58,44" stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="58" cy="44" r="3" fill={AMBER_DARK} />
        <path d="M 84,29 C 88,35 90,42 87,48" stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="87" cy="48" r="3" fill={AMBER_DARK} />
      </g>
    );
  }

  if (isThinking) {
    // Left perked + mic, right droops + plain circle
    return (
      <g>
        <path d="M 76,30 C 72,18 74,8  70,5"  stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <MicTip cx={70} cy={5} />
        <path d="M 84,29 C 90,36 93,43 91,49" stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="91" cy="49" r="3" fill={AMBER_DARK} />
      </g>
    );
  }

  // Default — relaxed springy curves, both mic tips
  return (
    <g>
      <path d="M 76,30 C 68,17 72,6  65,3"  stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <MicTip cx={65} cy={3} />
      <path d="M 84,28 C 90,15 93,8  89,3"  stroke={AMBER_DARK} strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <MicTip cx={89} cy={3} />
    </g>
  );
}

// ─── Eye ─────────────────────────────────────────────────────────────────────

function Eye({ mood }) {
  const scale   = (mood === 'excited' || mood === 'celebrating') ? 1.2 : 1;
  const cx      = 89;
  const cy      = 40;
  const pupilDx = mood === 'thinking' ? -2   : 0;
  const pupilDy = mood === 'sad'      ?  2.5 : 0;
  const lidRy   = mood === 'peeking'  ?  9   : mood === 'sad' ? 6 : 3;

  return (
    <g>
      <ellipse cx={cx} cy={cy} rx={10 * scale} ry={11 * scale} fill="white" />
      <ellipse cx={cx} cy={cy - 5 * scale} rx={8 * scale} ry={lidRy * scale} fill="#C4A0C0" opacity="0.35" />
      <circle  cx={cx} cy={cy} r={6.5 * scale} fill={AMBER} />
      <circle  cx={cx + pupilDx} cy={cy + pupilDy} r={3.8 * scale} fill={INDIGO} />
      <circle  cx={cx + 2.5 * scale} cy={cy - 3.5 * scale} r={1.7 * scale} fill="white" />
    </g>
  );
}

// ─── Mouth ───────────────────────────────────────────────────────────────────

function Mouth({ mood }) {
  if (mood === 'sad') {
    return (
      <path d="M 92,58 Q 96,53 100,57"
        stroke={AMBER_DARK} strokeWidth="2" fill="none" strokeLinecap="round" />
    );
  }
  if (mood === 'thinking') {
    return (
      <line x1="92" y1="55" x2="100" y2="55"
        stroke={AMBER_DARK} strokeWidth="2" strokeLinecap="round" />
    );
  }
  if (mood === 'excited' || mood === 'celebrating') {
    return (
      <g>
        <path d="M 90,52 Q 96,63 102,52"
          stroke={AMBER_DARK} strokeWidth="2" fill="none" strokeLinecap="round" />
        <ellipse cx="96" cy="57" rx="5" ry="4.5" fill={ERR_RED} opacity="0.75" />
      </g>
    );
  }
  return (
    <g>
      <path d="M 92,53 Q 96,61 101,54"
        stroke={AMBER_DARK} strokeWidth="2" fill="none" strokeLinecap="round" />
      <ellipse cx="96" cy="57" rx="3.5" ry="2.8" fill={ERR_RED} opacity="0.5" />
    </g>
  );
}

// ─── Special overlays ────────────────────────────────────────────────────────

function PartyHat() {
  return (
    <g>
      <polygon points="80,19 70,36 90,36" fill={INDIGO} />
      <polygon points="80,19 70,36 80,36" fill="#4540B5" opacity="0.5" />
      <circle cx="80" cy="19" r="3.5" fill={AMBER} />
      <line x1="70" y1="29" x2="90" y2="29" stroke={AMBER} strokeWidth="1.5" opacity="0.8" />
    </g>
  );
}

function Sparkles() {
  return (
    <g>
      <StarShape cx={110} cy={28} size={5} fill={AMBER}  />
      <StarShape cx={18}  cy={35} size={4} fill={INDIGO} />
      <StarShape cx={107} cy={57} size={4} fill={AMBER}  />
    </g>
  );
}

function StarShape({ cx, cy, size, fill }) {
  const pts = [];
  for (let i = 0; i < 4; i++) {
    const a = (i * 90 * Math.PI) / 180;
    pts.push(`${cx + Math.cos(a) * size},${cy + Math.sin(a) * size}`);
    const m = ((i * 90 + 45) * Math.PI) / 180;
    pts.push(`${cx + Math.cos(m) * size * 0.4},${cy + Math.sin(m) * size * 0.4}`);
  }
  return <polygon points={pts.join(' ')} fill={fill} />;
}

function getMoodAnimation(mood) {
  switch (mood) {
    case 'excited':     return 'animate-wiggle';
    case 'celebrating': return 'animate-bounce-in';
    case 'thinking':    return '';
    case 'peeking':     return '';
    default:            return 'animate-float';
  }
}

// ─── Convenience exports (same API as original Larry) ─────────────────────────

export function LarryGreeting({ name, className }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return (
    <Larry mood="happy" size={64} message={`${greeting}, ${name}!`} className={className} />
  );
}

export function LarryTip({ tip, className }) {
  return <Larry mood="thinking" size={48} message={tip} className={className} />;
}

export function LarryCelebration({ message, className }) {
  return (
    <Larry mood="celebrating" size={72} message={message || 'Amazing work!'} className={className} />
  );
}
